import * as THREE from 'three';
import { GameObject } from '../objects/GameObject';
import { Camera } from '../rendering/Camera';
import { Quadtree, Boundary } from './SpatialPartition';
import { Config } from '../core/Config';

/**
 * Manages frustum culling and hierarchical occlusion culling for efficient rendering
 */
export class FrustumCullingSystem {
  private static instance: FrustumCullingSystem | null = null;
  
  private camera: Camera;
  private frustum: THREE.Frustum = new THREE.Frustum();
  private projScreenMatrix: THREE.Matrix4 = new THREE.Matrix4();
  private cameraViewMatrix: THREE.Matrix4 = new THREE.Matrix4();
  private cameraProjectionMatrix: THREE.Matrix4 = new THREE.Matrix4();
  
  // Debug visualization
  private debugMode: boolean = false;
  private debugObjects: THREE.Object3D[] = [];
  private scene: THREE.Scene;
  
  // Tracking of visible objects
  private visibleObjects: Set<string> = new Set();
  private visibleQuadtreeNodes: Set<string> = new Set();
  
  // Tracking performance metrics
  private totalObjectsChecked: number = 0;
  private totalObjectsVisible: number = 0;
  private totalNodesChecked: number = 0;
  private totalNodesVisible: number = 0;
  
  private constructor(camera: Camera, scene: THREE.Scene) {
    this.camera = camera;
    this.scene = scene;
    console.log('FrustumCullingSystem initialized');
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(camera?: Camera, scene?: THREE.Scene): FrustumCullingSystem {
    if (!FrustumCullingSystem.instance && camera && scene) {
      FrustumCullingSystem.instance = new FrustumCullingSystem(camera, scene);
    } else if (!FrustumCullingSystem.instance && (!camera || !scene)) {
      throw new Error('FrustumCullingSystem not initialized. Provide a camera and scene on first call.');
    }
    
    return FrustumCullingSystem.instance as FrustumCullingSystem;
  }
  
  /**
   * Update the frustum from the current camera
   */
  private updateFrustum(): void {
    const threeCamera = this.camera.getThreeCamera();
    
    this.cameraViewMatrix.copy(threeCamera.matrixWorldInverse);
    this.cameraProjectionMatrix.copy(threeCamera.projectionMatrix);
    
    this.projScreenMatrix.multiplyMatrices(
      this.cameraProjectionMatrix,
      this.cameraViewMatrix
    );
    
    this.frustum.setFromProjectionMatrix(this.projScreenMatrix);
  }
  
  /**
   * Check if a point is in the frustum
   */
  public isPointInFrustum(point: THREE.Vector3): boolean {
    return this.frustum.containsPoint(point);
  }
  
  /**
   * Check if a sphere is in the frustum
   */
  public isSphereInFrustum(center: THREE.Vector3, radius: number): boolean {
    return this.frustum.intersectsSphere(new THREE.Sphere(center, radius));
  }
  
  /**
   * Check if a box is in the frustum
   */
  public isBoxInFrustum(box: THREE.Box3): boolean {
    return this.frustum.intersectsBox(box);
  }
  
  /**
   * Check if a quadtree boundary is in the frustum
   */
  public isBoundaryInFrustum(boundary: Boundary): boolean {
    // Create a box from the boundary
    const minX = boundary.x - boundary.width;
    const maxX = boundary.x + boundary.width;
    const minZ = boundary.z - boundary.height;
    const maxZ = boundary.z + boundary.height;
    
    // Create a box that represents the boundary (assuming y=0 to y=10 range for height)
    const box = new THREE.Box3(
      new THREE.Vector3(minX, 0, minZ),
      new THREE.Vector3(maxX, 10, maxZ)
    );
    
    return this.frustum.intersectsBox(box);
  }
  
  /**
   * Check if an object is in the frustum
   */
  public isObjectInFrustum(object: GameObject): boolean {
    const position = object.getPosition();
    // We're using a simple sphere-based culling for objects
    // Could be enhanced with actual object bounds in the future
    
    // Use a radius based on the object's scale
    const scale = object.getScale();
    const radius = Math.max(scale.x, scale.y, scale.z) * 2;
    
    return this.isSphereInFrustum(position, radius);
  }
  
  /**
   * Cull objects in a quadtree against the view frustum
   * Uses hierarchical approach for efficiency
   */
  public cullQuadtree(quadtree: Quadtree): GameObject[] {
    this.updateFrustum();
    
    // Reset counters for this frame
    this.totalObjectsChecked = 0;
    this.totalObjectsVisible = 0;
    this.totalNodesChecked = 0;
    this.totalNodesVisible = 0;
    
    // Clear previous visible sets
    this.visibleObjects.clear();
    this.visibleQuadtreeNodes.clear();
    
    // Begin recursive culling
    const visibleObjects: GameObject[] = [];
    this.cullQuadtreeNode(quadtree, visibleObjects);
    
    // Update debug visualizations if enabled
    if (this.debugMode) {
      this.updateDebugVisualizations(quadtree);
    }
    
    this.totalObjectsVisible = visibleObjects.length;
    
    return visibleObjects;
  }
  
  /**
   * Recursively cull a quadtree node and its children
   */
  private cullQuadtreeNode(node: Quadtree, visibleObjects: GameObject[]): void {
    this.totalNodesChecked++;
    
    // First check if the node's boundary is in the frustum
    if (!this.isBoundaryInFrustum(node['boundary'])) {
      // The entire node is outside the frustum, so we can skip it
      return;
    }
    
    // Node is at least partially visible
    this.totalNodesVisible++;
    // Generate a unique ID for the node using its boundary
    const boundary = node['boundary'];
    const nodeId = `${boundary.x},${boundary.z},${boundary.width},${boundary.height}`;
    this.visibleQuadtreeNodes.add(nodeId);
    
    // Check objects in this node
    const objects = node['objects'];
    this.totalObjectsChecked += objects.length;
    
    for (const object of objects) {
      if (this.isObjectInFrustum(object)) {
        visibleObjects.push(object);
        this.visibleObjects.add(object.getId());
      }
    }
    
    // Recursively check children if they exist
    if (node['divided']) {
      const children = [
        node['northwest'],
        node['northeast'],
        node['southwest'],
        node['southeast']
      ];
      
      for (const child of children) {
        if (child) {
          this.cullQuadtreeNode(child, visibleObjects);
        }
      }
    }
  }
  
  /**
   * Update debug visualizations for the culling system
   */
  private updateDebugVisualizations(quadtree: Quadtree): void {
    // Clear previous debug objects
    this.clearDebugVisualizations();
    
    // Visualize the frustum
    this.visualizeFrustum();
    
    // Visualize visible quadtree nodes
    this.visualizeVisibleQuadtreeNodes(quadtree);
  }
  
  /**
   * Visualize the camera frustum
   */
  private visualizeFrustum(): void {
    const frustumHelper = new THREE.CameraHelper(this.camera.getThreeCamera());
    this.scene.add(frustumHelper);
    this.debugObjects.push(frustumHelper);
  }
  
  /**
   * Visualize visible quadtree nodes
   */
  private visualizeVisibleQuadtreeNodes(quadtree: Quadtree): void {
    // Recursively visualize nodes
    this.visualizeNode(quadtree);
  }
  
  /**
   * Visualize a single quadtree node
   */
  private visualizeNode(node: Quadtree): void {
    const boundary = node['boundary'];
    const nodeId = `${boundary.x},${boundary.z},${boundary.width},${boundary.height}`;
    const isVisible = this.visibleQuadtreeNodes.has(nodeId);
    
    // Create box to represent the node
    const width = boundary.width * 2;
    const height = boundary.height * 2;
    const geometry = new THREE.BoxGeometry(width, 0.1, height);
    const material = new THREE.MeshBasicMaterial({
      color: isVisible ? 0x00ff00 : 0xff0000,
      transparent: true,
      opacity: 0.2,
      wireframe: true
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(boundary.x, 0.05, boundary.z);
    
    this.scene.add(mesh);
    this.debugObjects.push(mesh);
    
    // Recursively visualize children
    if (node['divided']) {
      const children = [
        node['northwest'],
        node['northeast'],
        node['southwest'],
        node['southeast']
      ];
      
      for (const child of children) {
        if (child) {
          this.visualizeNode(child);
        }
      }
    }
  }
  
  /**
   * Clear all debug visualization objects
   */
  private clearDebugVisualizations(): void {
    for (const obj of this.debugObjects) {
      this.scene.remove(obj);
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach(material => material.dispose());
        } else {
          obj.material.dispose();
        }
      }
    }
    
    this.debugObjects = [];
  }
  
  /**
   * Check if an object is visible (in frustum)
   */
  public isObjectVisible(objectId: string): boolean {
    return this.visibleObjects.has(objectId);
  }
  
  /**
   * Get all currently visible objects
   */
  public getVisibleObjects(): Set<string> {
    return this.visibleObjects;
  }
  
  /**
   * Enable or disable debug visualization
   */
  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    
    if (!enabled) {
      this.clearDebugVisualizations();
    }
  }
  
  /**
   * Toggle debug visualization
   */
  public toggleDebugMode(): void {
    this.setDebugMode(!this.debugMode);
  }
  
  /**
   * Clear all debug visualization objects
   * Public method that can be called by CullingManager for cleanup
   */
  public clearDebugObjects(): void {
    this.clearDebugVisualizations();
  }
  
  /**
   * Get performance statistics
   */
  public getStats(): { 
    objectsChecked: number, 
    objectsVisible: number, 
    nodesChecked: number, 
    nodesVisible: number 
  } {
    return {
      objectsChecked: this.totalObjectsChecked,
      objectsVisible: this.totalObjectsVisible,
      nodesChecked: this.totalNodesChecked,
      nodesVisible: this.totalNodesVisible
    };
  }
  
  /**
   * Check if an object is visible in the frustum and at what approximate distance
   * @param object GameObject to check visibility
   * @returns Object with visibility and distance data, or null if object has no valid position
   */
  public checkObjectVisibility(object: GameObject): { visible: boolean, distanceSquared: number } | null {
    // Update frustum first to ensure accurate checks
    this.updateFrustum();
    
    // Get object position
    const position = object.getPosition();
    if (!position) return null;
    
    // Create a temporary bounding sphere for this object
    // Using a fixed radius for simplicity - could be improved with actual object sizes
    const objectSize = 1.0; // Default object size
    const tempSphere = new THREE.Sphere(position, objectSize);
    
    // Check if sphere is in frustum
    const isVisible = this.frustum.intersectsSphere(tempSphere);
    
    // Calculate squared distance from camera (avoid sqrt for performance)
    const cameraPosition = this.camera.getPosition();
    const dx = position.x - cameraPosition.x;
    const dy = position.y - cameraPosition.y;
    const dz = position.z - cameraPosition.z;
    const distanceSquared = dx * dx + dy * dy + dz * dz;
    
    return { visible: isVisible, distanceSquared };
  }
  
  /**
   * Get the camera used by this frustum culling system
   */
  public getCamera(): Camera {
    return this.camera;
  }
} 